import React from "react"
import { useLingui } from "@lingui/react"
import { MainGuest, RealSuiteNumber } from "@ssc-booking/api-contracts"
import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import { t } from "@lingui/macro"
import { useRouter } from "next/router"
import { useECommerceMachine } from "../../../app-core/_common/machines/machines-context"
import {
    maybeSelectedSuiteCategory,
    SuiteCategoriesState,
    useSuiteCategoriesReadModel,
} from "../../../app-core/features/_common/machines/suite-categories-machine"
import { isPresent, stringIsPresent } from "../../../app-core/_common/checks"
import { BookingSteps } from "../../../app-core/_common/steps/booking-steps"
import { maybeFareCardByKey } from "../../../app-core/features/_common/read-models/fare"
import { ECommerceState } from "../../../app-core/features/_common/machines/ecommerce-machine/e-commerce-machine"
import { featureFlag } from "../../../app-core/_common/hooks/feature-flags"
import { noFlightKey } from "../../../app-core/features/flight/common/utils"
import StepRecap from "./StepRecap"
import { formatGuests, stepStatus } from "./steps-recap"

type Props = {
    activeStep: BookingSteps
}

const StepsRecapEcommerceV4: React.FC<Props> = ({ activeStep }) => {
    const { _ } = useLingui()
    const [state] = useECommerceMachine()
    const router = useRouter()
    const [stateSCRM] = useSuiteCategoriesReadModel(state)
    const flightStepEnabled = featureFlag("flights-step", router)

    const { guestsInfo, checkoutStatus } = state.context
    const selectedSuiteNumber = F.pipe(
        O.fromNullable(state.context.selectedSuiteNumber),
        O.chain(O.fromPredicate(RealSuiteNumber.strictType.is)),
    )

    const formattedGuests = formatGuests(state.context.guests)
    const formattedSuite = formatSuite(stateSCRM, selectedSuiteNumber)
    const formattedFare = formatFare(state)
    const formattedFlight = flightStepEnabled ? formatFlight(state) : undefined
    const formattedGuestInfo = formatGuestInfo(
        guestsInfo?.mainGuest,
        checkoutStatus.status !== "tobeValidated" ? _(t`Completed`) : undefined,
    )

    return (
        <div>
            <dl>
                <StepRecap
                    label={_(t`Guest selection`)}
                    value={formattedGuests}
                    state={stepStatus(formattedGuests, activeStep == "guestsV4")}
                    testId="guests"
                />
                <StepRecap
                    label={_(t`Suite Category`)}
                    value={formattedSuite}
                    state={stepStatus(formattedSuite, activeStep == "categoryV4")}
                    testId="suite-category"
                />
                <StepRecap
                    label={_(t`Fare type`)}
                    value={formattedFare}
                    state={stepStatus(formattedFare, activeStep == "fareV4")}
                    testId="fare"
                />
                {flightStepEnabled && (
                    <StepRecap
                        label={_(t`Flight package`)}
                        value={formattedFlight}
                        state={stepStatus(formattedFlight, activeStep == "addonsV4")}
                        testId="addons"
                    />
                )}
                <StepRecap
                    label={_(t`Guests info`)}
                    value={formattedGuestInfo}
                    state={stepStatus(formattedGuestInfo, activeStep == "guests-infoV4")}
                    testId="guests-info"
                />
                <StepRecap
                    label={_(t`Payment`)}
                    value={undefined}
                    state={stepStatus(undefined, activeStep == "paymentV4")}
                    testId="payment"
                />
            </dl>
        </div>
    )
}

const formatSuite = (stateSCRM: SuiteCategoriesState, selectedSuiteNumber: O.Option<RealSuiteNumber>) => {
    const suiteInfo = F.pipe(
        maybeSelectedSuiteCategory(stateSCRM),
        O.fold(
            () => undefined,
            (selectedSuiteCategory) => ({
                suiteName: selectedSuiteCategory.content.card.title,
                availability: selectedSuiteCategory.price.availability,
            }),
        ),
    )

    const maybeSuiteNumber = F.pipe(
        selectedSuiteNumber,
        O.map((suiteNumber) => `#${suiteNumber}`),
        O.toUndefined,
    )

    const suiteNumberOrGaranteed =
        suiteInfo?.availability == "guaranteed" ? ` (${t`Guaranteed category`})` : maybeSuiteNumber
    return [suiteInfo?.suiteName, suiteNumberOrGaranteed].filter(stringIsPresent).join(" ")
}

const formatFlight = (state: ECommerceState) => {
    const selectedFlightNumber = state.context.selectedFlightNumber
    if (selectedFlightNumber === noFlightKey) return t`No flight`
    return state.context.selectedFlightNumber //TODO: Implement flight format logic when data is available
}

const formatFare = (state: ECommerceState) => {
    const selectedFare = maybeFareCardByKey(state.context, state.context.selectedFare?.key)
    return O.toUndefined(selectedFare)?.fareLabel
}

const formatGuestInfo = (mainGuest: MainGuest | undefined, fallbackText?: string | undefined) => {
    if (!isPresent(mainGuest?.lastName)) return fallbackText

    return `${firstCharUpperCase(mainGuest.title)}. ${mainGuest?.firstName} ${mainGuest?.lastName}`
}

const firstCharUpperCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

export default StepsRecapEcommerceV4
